'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Star, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function LuxuryEleganceHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-gray-900 text-white font-serif">
      <header className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center p-6 md:p-8">
        <h2 className="text-2xl font-bold tracking-widest uppercase">Aurora</h2>
        <nav className="hidden md:flex gap-8 text-sm uppercase tracking-wider">
          <a href="#" className="text-gray-300 hover:text-white transition-colors">
            Estates
          </a>
          <a href="#" className="text-gray-300 hover:text-white transition-colors">
            Yachts
          </a>
          <a href="#" className="text-gray-300 hover:text-white transition-colors">
            Experiences
          </a>
          <a href="#" className="text-gray-300 hover:text-white transition-colors">
            Contact
          </a>
        </nav>
        <button
          className="md:hidden p-2 text-white"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </header>

      {isMenuOpen && (
        <div className="fixed inset-0 bg-gray-900/95 z-30 flex flex-col items-center justify-center md:hidden">
           <button
            className="absolute top-6 right-6 p-2 text-white"
            onClick={() => setIsMenuOpen(false)}
            >
                <X size={28} />
            </button>
          <nav className="flex flex-col text-center gap-8 text-2xl uppercase tracking-wider">
            <a href="#" className="hover:text-white transition-colors" onClick={() => setIsMenuOpen(false)}>
              Estates
            </a>
            <a href="#" className="hover:text-white transition-colors" onClick={() => setIsMenuOpen(false)}>
              Yachts
            </a>
            <a href="#" className="hover:text-white transition-colors" onClick={() => setIsMenuOpen(false)}>
              Experiences
            </a>
            <a href="#" className="hover:text-white transition-colors" onClick={() => setIsMenuOpen(false)}>
              Contact
            </a>
          </nav>
        </div>
      )}

      <main>
        <section className="relative h-screen flex items-center justify-center">
          <div className="absolute inset-0 bg-black opacity-50 z-10"></div>
          <Image
            src="https://picsum.photos/seed/newlux1/1920/1080"
            alt="A modern luxury villa with a pool at twilight"
            fill
            className="object-cover"
            priority
            data-ai-hint="luxury villa twilight"
          />
          <div className="relative text-center z-10 px-4">
            <h1 className="text-5xl md:text-8xl font-bold tracking-tighter text-white drop-shadow-2xl">
              Live Exquisite
            </h1>
            <p className="mt-4 text-lg max-w-2xl mx-auto text-gray-200">
              Discover a world of unparalleled luxury, where every detail is
              tailored to your desires.
            </p>
            <Button className="mt-8 bg-blue-600 text-white hover:bg-blue-500 rounded-none uppercase tracking-widest px-8 py-6 md:px-10 md:py-7 text-sm font-bold transition-colors">
              Explore Our Portfolio
            </Button>
          </div>
        </section>

        <section className="bg-gray-800 py-16 md:py-24 px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                An Uncompromising Standard
              </h2>
              <p className="mt-4 text-lg text-gray-400">
                AURORA curates the world's most exceptional assets and
                experiences for a discerning clientele.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="relative overflow-hidden group h-96">
                <Image
                  src="https://picsum.photos/seed/newlux-yacht/800/1000"
                  alt="Sleek modern superyacht on the ocean"
                  fill className="object-cover"
                  data-ai-hint="superyacht ocean"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                  <h3 className="text-2xl font-bold text-white">
                    Private Yachts
                  </h3>
                </div>
              </div>
              <div className="relative overflow-hidden group h-96">
                <Image
                  src="https://picsum.photos/seed/newlux-villa/800/1000"
                  alt="Modern villa overlooking the sea at sunset"
                   fill className="object-cover"
                  data-ai-hint="modern villa"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                  <h3 className="text-2xl font-bold text-white">
                    Exclusive Estates
                  </h3>
                </div>
              </div>
              <div className="relative overflow-hidden group h-96 md:col-span-2 lg:col-span-1">
                <Image
                  src="https://picsum.photos/seed/newlux-jet/800/1000"
                  alt="Interior of a luxurious private jet"
                  fill className="object-cover"
                  data-ai-hint="private jet"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                  <h3 className="text-2xl font-bold text-white">Air Travel</h3>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                What Our Clients Say
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div>
                <div className="flex justify-center text-blue-400 mb-4">
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                </div>
                <p className="text-lg italic text-gray-300">
                  "Aurora redefined our concept of luxury. Their attention to
                  detail is simply unmatched."
                </p>
                <p className="mt-4 font-bold uppercase tracking-wider text-sm text-gray-400">
                  - The Sterling Family
                </p>
              </div>
              <div>
                <div className="flex justify-center text-blue-400 mb-4">
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                </div>
                <p className="text-lg italic text-gray-300">
                  "A truly seamless and extraordinary experience from start to
                  finish. We will be clients for life."
                </p>
                <p className="mt-4 font-bold uppercase tracking-wider text-sm text-gray-400">
                  - Mr. Chen
                </p>
              </div>
              <div>
                <div className="flex justify-center text-blue-400 mb-4">
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                  <Star fill="currentColor" />
                </div>
                <p className="text-lg italic text-gray-300">
                  "The private yacht excursion was the highlight of our year.
                  Flawlessly executed."
                </p>
                <p className="mt-4 font-bold uppercase tracking-wider text-sm text-gray-400">
                  - Ms. Dubois
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gray-950 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white">
              The Aurora Difference
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-10">
              Service that anticipates. Access that is unparalleled.
            </p>
            <Button
              variant="outline"
              className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-gray-900 rounded-none uppercase tracking-widest px-8 py-6 md:px-10 md:py-7 text-sm font-bold transition-colors"
            >
              Request a Consultation
            </Button>
          </div>
        </section>
      </main>
      <footer className="bg-black border-t border-gray-800 py-12 px-4">
        <div className="container mx-auto text-center text-gray-400 text-sm">
          <p className="mb-4 text-lg font-bold tracking-widest text-white">
            AURORA
          </p>
          <div className="flex justify-center gap-6 mb-8">
            <a href="#" className="hover:text-white transition-colors">
              Twitter
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Instagram
            </a>
            <a href="#" className="hover:text-white transition-colors">
              LinkedIn
            </a>
          </div>
          <p>
            &copy; {new Date().getFullYear()} Aurora Luxury Holdings. All Rights
            Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
